<template>
  <div class="platform-new-data-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 筛选区域 -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <div class="filter-item">
            <label class="filter-label">N日内数据：</label>
            <el-select v-model="nDays" style="width: 130px;">
              <el-option v-for="o in nDaysOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">注册日期：</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              style="width: 260px;"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">渠道：</label>
            <el-select v-model="channel" style="width: 120px;">
              <el-option v-for="o in channelOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">版本：</label>
            <el-select v-model="versionFilter" style="width: 170px;">
              <el-option-group label="及之后">
                <el-option v-for="v in versionOptionList" :key="'gte:' + v" :label="v + ' 及之后'" :value="'gte:' + v" />
              </el-option-group>
              <el-option-group label="仅此版本">
                <el-option v-for="v in versionOptionList" :key="'eq:' + v" :label="'仅 ' + v" :value="'eq:' + v" />
              </el-option-group>
            </el-select>
          </div>

          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </div>
        <div class="filter-tip">
          <strong>本页仅统计 App 版本{{ versionTipText }}的用户与设备，其他版本用户不计入；1.0.16 起注册需绑定手机号：无手机号的 1.0.16 及之后版本用户不算注册成功，不计入本页任何用户指标（新增设备数不受影响，注册率随之下降为真实口径）。</strong><br />
          日期范围 = 注册日期范围（圈定该期间新增的用户/设备）；N日内 = 每个用户「注册当天(D0)起、到第 N 个自然日结束」窗口内的行为（0 日内 = 注册当天）。<br />
          除「注册率 = 新增用户数 / 新增设备数」外，其余「率」分母均为新增用户数。小说曝光/详情页为埋点口径：1.0.16 起新用户首启直连进书、不经列表与详情页，这两组仅反映「回到书城主动浏览」的行为，曝光用户数会明显小于新增用户数。<br />
          进入剧情 = 该书产生过对白记录（业务数据口径，不依赖埋点，覆盖首启直连进书）。付费 = 在 App 内消费能量（果肉，含 AI 互动解锁/道具/服装/AI 对话按条扣费），付费 N 能量率 = 累计消费 ≥N 果肉。节点完成 = 该节点及其剧情图上任一后继节点均产生过对白记录（同业务口径；各书最末节点无后继、不计入完成）。<br />
          章节完成 = 同对白口径下玩完该章，「完成任意 N 本第一/二/三章率」= 按用户跨书统计「完成该章的书数 ≥N」的人数；看广告充值 = 看广告获得果肉（每次约 12，「60 货币」≈「看 5 次」，两项可交叉验证埋点）；支付充值 = 真实支付成功，支付充值 5 元率 = 累计支付 ≥5 元。<br />
          充值金额 = 上述筛选范围内的合计收入：<strong>广告部分按每次观看折算 {{ adUnitPriceText }} 计</strong>（次数为总观看次数，非人数）；充值部分为真实支付成功金额；全部 = 两者相加。
        </div>
      </el-card>
    </div>

    <!-- 汇总指标 -->
    <div class="result-section">
      <h3 class="section-title">平台新增数据</h3>
      <div v-if="data" class="metric-groups">
        <el-card v-for="group in metricGroups" :key="group.title" shadow="hover" class="metric-card">
          <div slot="header" class="metric-card-title">{{ group.title }}</div>
          <div v-for="m in group.metrics" :key="m.label" class="metric-row">
            <span class="metric-label">{{ m.label }}</span>
            <span class="metric-value">
              {{ displayValue(m) }}
              <span v-if="subText(m)" class="metric-sub">{{ subText(m) }}</span>
            </span>
          </div>
        </el-card>
      </div>
      <div v-else class="empty-tip">暂无数据</div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { getPlatformNewData, getAppVersions, PlatformNewData, PlatformNewDataParams } from '@/api/platformNewData'

// 版本筛选默认值：复合值 'gte:1.0.16'（及之后）/ 'eq:1.0.16'（仅此版本），请求时拆成 version + version_mode
const DEFAULT_VERSION = '1.0.16'
const DEFAULT_VERSION_FILTER = `gte:${DEFAULT_VERSION}`

interface MetricDef {
  label: string
  type: 'count' | 'rate' | 'money'         // money：后端以「分」下发，展示成元
  field?: keyof PlatformNewData            // count / money 用
  num?: keyof PlatformNewData              // rate 分子
  den?: keyof PlatformNewData              // rate 分母
  sub?: (d: PlatformNewData) => string     // 自定义副文案（默认只有 rate 显示分子人数）
}

@Component
export default class PlatformNewDataView extends Vue {
  // 仅用于吸收 router-view 透传的 prop，避免 attribute fallthrough，本页不使用
  @Prop() readonly game?: unknown
  @Prop() readonly games?: unknown

  loading = false
  nDays: 0 | 1 | 3 | 7 | 14 | 30 = 0
  channel = 'all'
  versionFilter = DEFAULT_VERSION_FILTER
  appVersions: string[] = []
  dateRange: string[] = []
  data: PlatformNewData | null = null

  // 下拉版本列表：后端语义降序；接口失败/未含默认版本（未上报）时把默认版本补到最前，保证默认值可见可选
  get versionOptionList(): string[] {
    return this.appVersions.includes(DEFAULT_VERSION) ? this.appVersions : [DEFAULT_VERSION, ...this.appVersions]
  }

  // 口径说明里的广告单价文案。取自接口而非写死：改了后端常量这里跟着变，
  // 不会出现「说明写 6 分、实际按别的算」
  get adUnitPriceText(): string {
    if (!this.data) return '固定单价'
    return `${(this.data.ad_unit_price_fen / 100).toFixed(2)} 元（${this.data.ad_unit_price_fen} 分）`
  }

  // 口径说明里的当前所选版本文案，如「 ≥ 1.0.16」/「 = 1.0.16」
  get versionTipText(): string {
    const [mode, ver] = this.versionFilter.split(':')
    return mode === 'eq' ? ` = ${ver}` : ` ≥ ${ver}`
  }

  nDaysOptions = [
    { value: 0, label: '0日内（当天）' },
    { value: 1, label: '1日内' },
    { value: 3, label: '3日内' },
    { value: 7, label: '7日内' },
    { value: 14, label: '14日内' },
    { value: 30, label: '30日内' }
  ]

  channelOptions = [
    { value: 'toutiao', label: '头条' },
    { value: 'kuaishou', label: '快手' },
    { value: 'other', label: '其他' },
    { value: 'all', label: '所有' }
  ]

  // 指标分组：count 直接展示数值；rate 展示 分子/分母 的百分比（并附原始分子人数）
  metricGroups: { title: string; metrics: MetricDef[] }[] = [
    {
      title: '新增',
      metrics: [
        { label: '新增设备数', type: 'count', field: 'new_devices' },
        { label: '新增用户数', type: 'count', field: 'new_users' },
        { label: '注册率', type: 'rate', num: 'new_users', den: 'new_devices' }
      ]
    },
    {
      title: '小说曝光 / 详情页',
      metrics: [
        { label: '小说列表曝光用户数', type: 'count', field: 'impression_users' },
        { label: '进入≥1本小说详情页率', type: 'rate', num: 'detail_view_ge1', den: 'new_users' },
        { label: '进入≥2本小说详情页率', type: 'rate', num: 'detail_view_ge2', den: 'new_users' },
        { label: '进入≥3本小说详情页率', type: 'rate', num: 'detail_view_ge3', den: 'new_users' }
      ]
    },
    {
      title: '进入剧情',
      metrics: [
        { label: '进入≥1本小说剧情内率', type: 'rate', num: 'enter_ge1', den: 'new_users' },
        { label: '进入≥2本小说剧情内率', type: 'rate', num: 'enter_ge2', den: 'new_users' },
        { label: '进入≥3本小说剧情内率', type: 'rate', num: 'enter_ge3', den: 'new_users' }
      ]
    },
    {
      title: '节点完成',
      metrics: [
        { label: '完成≥1个节点率', type: 'rate', num: 'node_ge1', den: 'new_users' },
        { label: '完成≥2个节点率', type: 'rate', num: 'node_ge2', den: 'new_users' },
        { label: '完成≥3个节点率', type: 'rate', num: 'node_ge3', den: 'new_users' }
      ]
    },
    {
      title: 'AI节点完成',
      metrics: [
        { label: '完成≥1个AI节点率', type: 'rate', num: 'ai_node_ge1', den: 'new_users' },
        { label: '完成≥2个AI节点率', type: 'rate', num: 'ai_node_ge2', den: 'new_users' },
        { label: '完成≥3个AI节点率', type: 'rate', num: 'ai_node_ge3', den: 'new_users' }
      ]
    },
    {
      title: '章节完成',
      metrics: [
        { label: '完成任意1本小说第一章率', type: 'rate', num: 'chapter1_book_ge1', den: 'new_users' },
        { label: '完成任意2本小说第一章率', type: 'rate', num: 'chapter1_book_ge2', den: 'new_users' },
        { label: '完成任意1本小说第二章率', type: 'rate', num: 'chapter2_book_ge1', den: 'new_users' },
        { label: '完成任意2本小说第二章率', type: 'rate', num: 'chapter2_book_ge2', den: 'new_users' },
        { label: '完成任意1本小说第三章率', type: 'rate', num: 'chapter3_book_ge1', den: 'new_users' },
        { label: '完成任意2本小说第三章率', type: 'rate', num: 'chapter3_book_ge2', den: 'new_users' }
      ]
    },
    {
      title: '付费（能量消费）',
      metrics: [
        { label: '付费率', type: 'rate', num: 'pay0', den: 'new_users' },
        { label: '付费10能量率', type: 'rate', num: 'pay10', den: 'new_users' },
        { label: '付费20能量率', type: 'rate', num: 'pay20', den: 'new_users' },
        { label: '付费30能量率', type: 'rate', num: 'pay30', den: 'new_users' },
        { label: '付费50能量率', type: 'rate', num: 'pay50', den: 'new_users' },
        { label: '付费100能量率', type: 'rate', num: 'pay100', den: 'new_users' },
        { label: '付费300能量率', type: 'rate', num: 'pay300', den: 'new_users' }
      ]
    },
    {
      title: '看广告充值',
      metrics: [
        { label: '看广告充值率', type: 'rate', num: 'ad_reward_users', den: 'new_users' },
        { label: '看广告充值60货币率', type: 'rate', num: 'ad_reward_60', den: 'new_users' }
      ]
    },
    {
      title: '支付充值',
      metrics: [
        { label: '支付充值率', type: 'rate', num: 'recharge_users', den: 'new_users' },
        { label: '支付充值5元率', type: 'rate', num: 'recharge_5yuan', den: 'new_users' }
      ]
    },
    {
      title: '充值金额',
      metrics: [
        {
          label: '总充值金额（广告）',
          type: 'money',
          field: 'ad_revenue_fen',
          // 单价取自接口而不是写死，标注和后端算法不会对不上
          sub: d => `${d.ad_view_count.toLocaleString()} 次 × ${(d.ad_unit_price_fen / 100).toFixed(2)} 元`
        },
        {
          label: '总充值金额（充值）',
          type: 'money',
          field: 'recharge_amount_fen',
          sub: d => `${d.recharge_users.toLocaleString()} 人支付`
        },
        {
          label: '总充值金额（全部）',
          type: 'money',
          field: 'total_revenue_fen',
          sub: () => '广告 + 充值'
        }
      ]
    }
  ]

  created() {
    this.dateRange = this.defaultRange()
    this.loadVersions()
    this.handleSearch()
  }

  async loadVersions() {
    try {
      this.appVersions = await getAppVersions()
    } catch (error) {
      // 拉不到就用 versionOptionList 的默认兜底项
      console.error('获取版本列表失败:', error)
    }
  }

  // 默认日期范围：今天的 -1 ~ -7 天
  defaultRange(): string[] {
    const today = new Date()
    const end = new Date(today)
    end.setDate(end.getDate() - 1)
    const start = new Date(today)
    start.setDate(start.getDate() - 7)
    return [this.fmt(start), this.fmt(end)]
  }

  fmt(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  formatPercent(value: number): string {
    if (value === undefined || value === null || isNaN(value)) return '-'
    return (value * 100).toFixed(1) + '%'
  }

  formatCount(value: number): string {
    if (value === undefined || value === null) return '-'
    return value.toLocaleString()
  }

  // 后端以「分」下发，这里才转成元。整数进整数出，不参与任何中间浮点运算
  formatMoney(fen: number): string {
    if (fen === undefined || fen === null || isNaN(fen)) return '-'
    return '¥' + (fen / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  rate(numerator: number, denominator: number): number {
    if (!denominator) return NaN
    return numerator / denominator
  }

  // 指标主值：count → 整数；rate → 百分比
  displayValue(m: MetricDef): string {
    if (!this.data) return '-'
    if (m.type === 'count') {
      return this.formatCount(this.data[m.field as keyof PlatformNewData])
    }
    if (m.type === 'money') {
      return this.formatMoney(this.data[m.field as keyof PlatformNewData])
    }
    return this.formatPercent(
      this.rate(this.data[m.num as keyof PlatformNewData], this.data[m.den as keyof PlatformNewData])
    )
  }

  // 副文案：优先用指标自带的；否则比率类默认附原始分子人数，便于核对
  subText(m: MetricDef): string {
    if (!this.data) return ''
    if (m.sub) return m.sub(this.data)
    if (m.type !== 'rate') return ''
    return `${this.formatCount(this.data[m.num as keyof PlatformNewData])} 人`
  }

  async handleSearch() {
    if (!this.dateRange || this.dateRange.length !== 2) {
      this.$message.warning('请选择注册日期范围')
      return
    }
    this.loading = true
    try {
      const [mode, ver] = this.versionFilter.split(':')
      this.data = await getPlatformNewData({
        n_days: this.nDays,
        start_date: this.dateRange[0],
        end_date: this.dateRange[1],
        channel: this.channel as PlatformNewDataParams['channel'],
        version: ver,
        version_mode: mode as PlatformNewDataParams['version_mode']
      })
    } catch (error) {
      // 错误已由 serviceGo 响应拦截器统一弹窗
      console.error('获取平台新增数据失败:', error)
    } finally {
      this.loading = false
    }
  }

  handleReset() {
    this.nDays = 0
    this.channel = 'all'
    this.versionFilter = DEFAULT_VERSION_FILTER
    this.dateRange = this.defaultRange()
    this.handleSearch()
  }
}
</script>

<style scoped>
.platform-new-data-wrapper {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
}

.filter-label {
  margin-right: 8px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.filter-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

.result-section {
  margin-bottom: 20px;
}

.section-title {
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.metric-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.metric-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-label {
  font-size: 13px;
  color: #606266;
}

.metric-value {
  font-size: 16px;
  font-weight: 600;
  color: #409eff;
  white-space: nowrap;
}

.metric-sub {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: #c0c4cc;
}

.empty-tip {
  padding: 40px;
  text-align: center;
  color: #909399;
  background: #fff;
  border-radius: 4px;
}
</style>
